import { useMemo } from 'react'

import { RotateCcw, UserMinus } from 'lucide-react'

import { AddressDisplay, UserDisplay } from '~/components/component-library'
import { UserPopover } from '~/components/component-library/UserDisplay/components/UserPopover'
import { useAccountUpdatedByMerkleTxn } from '~/components/txn-display/state-change/hooks/useAccountUpdatedByMerkleTxn'
import { MerkleStateChangeItem } from '~/components/txn-display/state-change/types'
import { useActiveOrg } from '~/hooks/org'
import {
  AuthOrg,
  BackendSmartAccount,
  BackendTransaction,
  BasicTransactionType,
  MerkleTransactionCallType,
  OrgMember,
  OrgMerkleTransaction,
  OrgSigner,
} from '~/types'
import { plural } from '~/utils/display'

type SignerWithUser = {
  signer: OrgSigner
  user?: OrgMember
}

const getRecoverySigners = (
  transaction: OrgMerkleTransaction,
  smartAccount: BackendSmartAccount,
  activeOrg: AuthOrg,
): SignerWithUser[] => {
  const removedSignerIds = new Set(
    transaction.txData.transactions
      .filter((tx) => tx.type === MerkleTransactionCallType.RemoveSigner)
      .map((tx) => tx.authenticatorId),
  )

  const existingSigners = (smartAccount.signers as OrgSigner[])
    .filter((s) => !removedSignerIds.has(s.id))
    .map((signer) => ({
      signer,
      user: activeOrg.members.find(({ id }) => id === signer.user?.id),
    }))

  const existingSignerIds = new Set(existingSigners.map((s) => s.signer.id))

  const addedSigners = transaction.txData.transactions
    .filter((tx) => tx.type === MerkleTransactionCallType.AddSigner)
    .filter((tx) => !existingSignerIds.has(tx.authenticatorId))
    .map((tx) => ({
      signer: {
        id: tx.authenticatorId,
        name: tx.name,
        displayName: tx.name,
      } as OrgSigner,
      user: activeOrg.members.find(({ email }) => email === tx.email),
    }))

  return [...existingSigners, ...addedSigners]
}

const SignerItem = ({ signer, user }: SignerWithUser) => {
  if (user) {
    return (
      <UserPopover
        user={user}
        align="start"
        optionalSigner={signer as unknown as OrgSigner}
      >
        <span className="inline-flex items-center justify-start gap-1">
          <div>
            <UserDisplay
              user={user}
              layout="standard"
              size="small"
              hidePopover
            />
          </div>
          <span className="text-left text-secondary">
            ({signer.displayName})
          </span>
        </span>
      </UserPopover>
    )
  }
  return <span>{signer.displayName}</span>
}

export const useRecoveryUpdate = ({
  transaction,
}: {
  transaction: BackendTransaction
}): MerkleStateChangeItem[] => {
  const account = useAccountUpdatedByMerkleTxn(transaction, {
    includeAutomations: true,
  })
  const { org: activeOrg } = useActiveOrg()

  return useMemo(() => {
    if (!transaction.types.includes(BasicTransactionType.RecoverAccount)) {
      return []
    }

    if (!activeOrg || !account) {
      return [
        {
          title: 'Reset signers',
          icon: <RotateCcw />,
          description: 'Sign into this workspace to see details',
        },
      ]
    }

    const merkleTxn = transaction as OrgMerkleTransaction
    const allSigners = getRecoverySigners(merkleTxn, account, activeOrg)
    const threshold =
      merkleTxn.txData.transactions.find(
        (tx) => tx.type === MerkleTransactionCallType.UpdateThreshold,
      )?.newThreshold ?? account?.threshold

    const removeEOASignerItems: MerkleStateChangeItem[] =
      merkleTxn.txData.transactions
        .filter((tx) => tx.type === MerkleTransactionCallType.RemoveEOASigner)
        .map((tx) => ({
          title: (
            <span>
              Remove EOA signer from{' '}
              <AddressDisplay address={account.address} layout="inline-text" />
            </span>
          ),
          icon: <UserMinus />,
          description: (
            <AddressDisplay address={tx.address} layout="inline-text" />
          ),
        }))

    return [
      {
        title: (
          <span>
            Reset signers on{' '}
            <AddressDisplay address={account.address} layout="inline-text" />
          </span>
        ),
        icon: <RotateCcw />,
        description: (
          <div className="flex flex-col gap-1.5">
            <span>
              Transactions will require {threshold}{' '}
              {plural('approval', threshold)} from:
            </span>
            {allSigners.map(({ signer, user }) => (
              <SignerItem key={signer.id} signer={signer} user={user} />
            ))}
          </div>
        ),
      },
      ...removeEOASignerItems,
    ]
  }, [transaction, account, activeOrg])
}
